/**
 * Widget Config API
 * ვიჯეტის კონფიგურაციის API
 */

import { get, post, put, del } from '@aiia/portal-core';

export interface WidgetTheme {
  primaryColor: string;
  primaryHoverColor?: string;
  backgroundColor: string;
  textColor: string;
  textLightColor?: string;
  userMessageColor: string;
  userMessageTextColor?: string;
  botMessageColor: string;
  botMessageTextColor?: string;
  borderColor?: string;
  errorColor?: string;
  borderRadius: string;
  fontFamily?: string;
  fontSize?: string;
}

export interface QuickAction {
  label: string;
  message: string;
  icon?: string;
}

export interface HeaderConfig {
  show: boolean;
  showCloseButton: boolean;
  statusText?: string;
  avatarUrl?: string;
  avatarEmoji?: string;
  backgroundColor?: string;
}

export interface LayoutConfig {
  width: number;
  height: number;
  offsetX?: number;
  offsetY?: number;
}

export interface ToggleButtonConfig {
  size: number;
  backgroundColor?: string;
  icon?: string;
  showBadge?: boolean;
  pulseAnimation?: boolean;
}

export interface MessagesConfig {
  showTimestamps: boolean;
  timestampFormat?: 'short' | 'long' | 'relative';
  showBotAvatar?: boolean;
  botAvatarUrl?: string;
  botAvatarEmoji?: string;
  typingText?: string;
}

export interface BrandingConfig {
  companyName?: string;
  logoUrl?: string;
  logoHeight?: string;
  showPoweredBy: boolean;
  poweredByText?: string;
}

export interface I18nConfig {
  language?: string;
  direction?: 'ltr' | 'rtl';
  strings?: Record<string, string>;
}

export interface WidgetConfig {
  id: string;
  userId: string;
  apiKeyId?: string;
  name: string;
  assistantName: string;
  logoUrl?: string;
  avatarUrl?: string;
  greeting: string;
  placeholder: string;
  position: 'bottom-right' | 'bottom-left' | 'center-bottom' | 'inline';
  theme: WidgetTheme;
  quickActions: QuickAction[];
  soundEnabled: boolean;
  isDefault: boolean;
  enabled: boolean;
  header?: HeaderConfig;
  layout?: LayoutConfig;
  toggleButton?: ToggleButtonConfig;
  messages?: MessagesConfig;
  branding?: BrandingConfig;
  i18n?: I18nConfig;
  // AI Configuration
  promptId?: string;
  // Tool and File attachments for AI/RAG
  toolIds?: string[];
  fileIds?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateWidgetConfigRequest {
  name: string;
  assistantName?: string;
  logoUrl?: string;
  avatarUrl?: string;
  greeting?: string;
  placeholder?: string;
  position?: 'bottom-right' | 'bottom-left' | 'center-bottom' | 'inline';
  theme?: Partial<WidgetTheme>;
  quickActions?: QuickAction[];
  soundEnabled?: boolean;
  isDefault?: boolean;
  header?: Partial<HeaderConfig>;
  layout?: Partial<LayoutConfig>;
  toggleButton?: Partial<ToggleButtonConfig>;
  messages?: Partial<MessagesConfig>;
  branding?: Partial<BrandingConfig>;
  i18n?: Partial<I18nConfig>;
}

export interface UpdateWidgetConfigRequest extends CreateWidgetConfigRequest {}

// Get all widget configs
export const getWidgetConfigs = () =>
  get<WidgetConfig[]>('/widget-configs');

// Get widget config by ID
export const getWidgetConfig = (id: string) =>
  get<WidgetConfig>(`/widget-configs/${id}`);

// Get default widget config
export const getDefaultWidgetConfig = () =>
  get<WidgetConfig>('/widget-configs/default');

// Create widget config
export const createWidgetConfig = (data: CreateWidgetConfigRequest) =>
  post<WidgetConfig>('/widget-configs', data);

// Update widget config
export const updateWidgetConfig = (id: string, data: UpdateWidgetConfigRequest) =>
  put<WidgetConfig>(`/widget-configs/${id}`, data);

// Set as default
export const setDefaultWidgetConfig = (id: string) =>
  put<WidgetConfig>(`/widget-configs/${id}/set-default`, {});

// Enable widget config
export const enableWidgetConfig = (id: string) =>
  put<void>(`/widget-configs/${id}/enable`, {});

// Disable widget config
export const disableWidgetConfig = (id: string) =>
  put<void>(`/widget-configs/${id}/disable`, {});

// Delete widget config
export const deleteWidgetConfig = (id: string) =>
  del<void>(`/widget-configs/${id}`);

// ============== Prompt Management ==============

// Assign a prompt to a widget
export const assignWidgetPrompt = (widgetId: string, promptId: string) =>
  put<void>(`/widget-configs/${widgetId}/prompt`, { promptId });

// Unassign prompt from a widget
export const unassignWidgetPrompt = (widgetId: string) =>
  del<void>(`/widget-configs/${widgetId}/prompt`);

// ============== Tool Management ==============

// Get tool IDs for a widget
export const getWidgetToolIds = (widgetId: string) =>
  get<string[]>(`/widget-configs/${widgetId}/tools`);

// Set tools for a widget (replaces existing)
export const setWidgetTools = (widgetId: string, toolIds: string[]) =>
  put<void>(`/widget-configs/${widgetId}/tools`, { toolIds });

// Add a tool to a widget
export const addWidgetTool = (widgetId: string, toolId: string) =>
  post<void>(`/widget-configs/${widgetId}/tools/${toolId}`, {});

// Remove a tool from a widget
export const removeWidgetTool = (widgetId: string, toolId: string) =>
  del<void>(`/widget-configs/${widgetId}/tools/${toolId}`);

// ============== File Management (RAG) ==============

// Get file IDs for a widget
export const getWidgetFileIds = (widgetId: string) =>
  get<string[]>(`/widget-configs/${widgetId}/files`);

// Set files for a widget (replaces existing)
export const setWidgetFiles = (widgetId: string, fileIds: string[]) =>
  put<void>(`/widget-configs/${widgetId}/files`, { fileIds });

// Add a file to a widget
export const addWidgetFile = (widgetId: string, fileId: string) =>
  post<void>(`/widget-configs/${widgetId}/files/${fileId}`, {});

// Remove a file from a widget
export const removeWidgetFile = (widgetId: string, fileId: string) =>
  del<void>(`/widget-configs/${widgetId}/files/${fileId}`);
